import { Controller } from "@hotwired/stimulus"
import { motionMs } from "../lib/motion"
import { getI18nMessage } from "../lib/i18n_helper"
import { lockScroll, unlockScroll } from "../lib/scroll_lock"

// Modal Controller (Global Singleton Pattern)
// Reusable modal with fetch + innerHTML loading
//
// Features:
// - Global singleton (one modal container in layout)
// - Fetch + innerHTML for content loading
// - Loading spinner during fetch
// - Backdrop click to close
// - Explicit close (×) button
// - No swipe gestures (mobile-first)
export default class extends Controller {
  static targets = ["backdrop", "panel", "title", "body"]
  static values = {
    url: String,
    title: String
  }

  connect() {
    // Store dialog ID (extract from element ID: "my-modal-backdrop" → "my-modal")
    this.dialogId = this.element.id.replace(/-backdrop$/, '')

    // Pre-load i18n messages (called once on connect)
    // Only load if this is a URL-based modal (has urlValue)
    if (this.hasUrlValue) {
      this.messages = {
        loadFailed: getI18nMessage(this.element, 'errorsLoadFailed', '載入失敗'),
        networkError: getI18nMessage(this.element, 'errorsNetworkError', '網路連線異常，請檢查後重試'),
        retryButton: getI18nMessage(this.element, 'actionsRetry', '重新載入')
      }
    } else {
      // Bottom sheet dialogs don't need error messages (no URL fetching)
      this.messages = null
    }
  }

  // Open modal and fetch content (for URL-based modals)
  async open(event) {
    // Check if this is a custom dialog:open event
    if (event?.detail?.dialogId) {
      // Only respond if this is the target dialog
      if (event.detail.dialogId !== this.dialogId) {
        return
      }
      // For category picker and other custom dialogs, just show without fetching
      this.show()
      return
    }

    // Original URL-based modal behavior
    event?.preventDefault()
    this.showLoading()
    this.show()

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const html = await response.text()
      this.bodyTarget.innerHTML = html

      if (this.titleValue) {
        this.titleTarget.textContent = this.titleValue
      }
    } catch (error) {
      console.error("Modal content fetch failed:", error)

      // Use messages if available, otherwise use fallbacks
      const loadFailedMsg = this.messages?.loadFailed || '載入失敗'
      const networkErrorMsg = this.messages?.networkError || '網路連線異常，請檢查後重試'
      const retryButtonMsg = this.messages?.retryButton || '重新載入'

      this.bodyTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-8 px-4">
          <svg class="h-12 w-12 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-sm text-charcoal-800 font-medium mb-2">${loadFailedMsg}</p>
          <p class="text-xs text-charcoal-600 mb-6">${networkErrorMsg}</p>
          <button type="button"
                  data-action="click->modal#retry"
                  class="inline-flex items-center justify-center h-11 px-6 rounded-full bg-crust-600 text-white text-sm font-semibold hover:bg-crust-700 active:scale-[0.97] transition-all shadow-sm"
                  style="min-height: 44px; min-width: 44px;">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            ${retryButtonMsg}
          </button>
        </div>
      `
    }
  }

  // Show modal with animation
  show() {
    this.element.classList.remove("hidden")
    lockScroll(this, { dismiss: () => this.close() })

    // Check if this is a bottom sheet dialog (has panel target)
    if (this.hasPanelTarget) {
      requestAnimationFrame(() => {
        // Bottom sheet slide-up animation
        this.element.style.opacity = "1"
        this.panelTarget.classList.remove("translate-y-full")
        this.panelTarget.classList.add("translate-y-0")
      })
    } else {
      // Original modal behavior (for URL-based modals)
      requestAnimationFrame(() => {
        this.backdropTarget.style.opacity = "1"
        this.panelTarget.classList.remove("opacity-0", "scale-95", "pointer-events-none")
        this.panelTarget.classList.add("opacity-100", "scale-100")
      })
    }
  }

  // Close modal with animation
  close(event) {
    // Check if this is a custom dialog:close event
    if (event?.detail?.dialogId) {
      // Only respond if this is the target dialog
      if (event.detail.dialogId !== this.dialogId) {
        return
      }
    } else {
      event?.preventDefault()
    }

    // Check if this is a bottom sheet dialog
    if (this.hasPanelTarget) {
      // Bottom sheet slide-down animation
      this.element.style.opacity = "0"
      this.panelTarget.classList.remove("translate-y-0")
      this.panelTarget.classList.add("translate-y-full")

      setTimeout(() => {
        this.element.classList.add("hidden")
        unlockScroll(this)
      }, motionMs("BASE_MS")) // Matches the --motion-base surface transition
    } else {
      // Original modal behavior (for URL-based modals)
      if (this.hasBackdropTarget) {
        this.backdropTarget.style.opacity = "0"
      }
      if (this.hasPanelTarget) {
        this.panelTarget.classList.remove("opacity-100", "scale-100")
        this.panelTarget.classList.add("opacity-0", "scale-95", "pointer-events-none")
      }

      setTimeout(() => {
        this.element.classList.add("hidden")
        unlockScroll(this)
        if (this.hasBodyTarget) {
          this.bodyTarget.innerHTML = ""
        }
        if (this.hasTitleTarget) {
          this.titleTarget.textContent = ""
        }
      }, motionMs("BASE_MS")) // Matches the --motion-base surface transition
    }
  }

  // Show loading spinner (only for URL-based modals)
  showLoading() {
    if (this.hasBodyTarget) {
      this.bodyTarget.innerHTML = `
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-crust-600"></div>
        </div>
      `
    }
  }

  // Handle backdrop click to close
  backdropClick(event) {
    if (event.target === this.backdropTarget) {
      this.close()
    }
  }

  // Stop event propagation (prevent backdrop click when clicking panel)
  stopPropagation(event) {
    event.stopPropagation()
  }

  // Retry failed content fetch
  retry(event) {
    event?.preventDefault()
    this.open(event)
  }
}
